import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { I18nProvider } from "@lingui/react";
import { RegrelloIcon, RegrelloNonIdealState } from "@regrello/ui-core";
import { withProfiler } from "@sentry/react";
import { useAtom, useAtomValue } from "jotai";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { useAsync, useMount } from "react-use";

import { initTranslation } from "./i18n";
import { FeatureFlagService } from "./services/FeatureFlagService";
import { isAuthenticatedAtom, isWaitingForAuth0AuthenticationAtom, userThemeAtom } from "./state/applicationState";
import { RegrelloAuthenticationProvider } from "./ui2/app/init/RegrelloAuthenticationProvider";
import { RoutePaths } from "./ui2/app/routes/consts";
import { getWorkspaceRouteTo } from "./ui2/app/routes/routeCreatorUtils";
import { UnauthenticatedCredentialsErrorPage } from "./ui2/auth/unauthenticatedPages/UnauthenticatedCredentialsErrorPage";
import { UnauthenticatedEmailSentPage } from "./ui2/auth/unauthenticatedPages/UnauthenticatedEmailSentPage";
import { UnauthenticatedMFAResetPage } from "./ui2/auth/unauthenticatedPages/UnauthenticatedMFAResetPage";
import { UnauthenticatedPasswordReset } from "./ui2/auth/unauthenticatedPages/UnauthenticatedPasswordReset";
import { UnauthenticatedRequestForEmailPage } from "./ui2/auth/unauthenticatedPages/UnauthenticatedRequestForEmailPage";
import { UnauthenticatedSignInPage } from "./ui2/auth/unauthenticatedPages/UnauthenticatedSignInPage";
import { UnauthenticatedSignInSsoPage } from "./ui2/auth/unauthenticatedPages/UnauthenticatedSignInSsoPage";
import { UnauthenticatedSignUpPage } from "./ui2/auth/unauthenticatedPages/UnauthenticatedSignUpPage";
import { UnsubscribePage } from "./ui2/auth/unauthenticatedPages/UnsubscribePage";
import { initCustomFieldPluginRegistrar } from "./ui2/shared/molecules/customFields/plugins/registry/initCustomFieldPluginRegistrar";
import { initSpectrumFieldPluginRegistrar } from "./ui2/shared/molecules/spectrumFields/registry/initSpectrumFieldPluginRegistrar";
import { ConfirmVerification } from "./ui2/tasks/webform/ConfirmVerification/ConfirmVerification";
import { VerificationPage } from "./ui2/tasks/webform/verificationPage/VerificationPage";
import { RegrelloSessionStorageKey, sessionStorageSetTyped } from "./utils/getFromSessionStorage";
import { useErrorLogger } from "./utils/hooks/useErrorLogger";
import { useIframeThemeChangeListener } from "./utils/hooks/useIframeThemeChangeListener";
import { MonitorAndReportFpsPerformance } from "./utils/MonitorAndReportFpsPerformance";

const LazyAppWithAuthProvider = React.lazy(() =>
  // eslint-disable-next-line lingui/no-unlocalized-strings
  import("./AppWithAuthProvider").then((module) => ({
    default: module.AppWithAuthProvider,
  })),
);

const LazyWebformPage = React.lazy(() =>
  // eslint-disable-next-line lingui/no-unlocalized-strings
  import("./ui2/tasks/webform/WebformPage").then((module) => ({
    default: module.WebformPage,
  })),
);

// TODO (clewis): Use Sentry.useProfiler hook.
export const App = withProfiler(function AppFn() {
  const { logErrorMessage } = useErrorLogger();

  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const [isWaitingForAuth0Authentication, setIsWaitingForAuth0Authentication] = useAtom(
    isWaitingForAuth0AuthenticationAtom,
  );

  const [hasFeatureFlagError, setHasFeatureFlagError] = useState(false);

  const userTheme = useAtomValue(userThemeAtom);
  useEffect(() => {
    document.documentElement.dataset.theme = userTheme;
  }, [userTheme]);

  // If we're in an iframe, listen for changes to the parent page's theme so we stay in sync.
  useIframeThemeChangeListener();

  // If true, allow login spoofing (dev only).
  const [isLoginSpoofingEnabled, setIsLoginSpoofingEnabled] = useState(false);

  const handleLoginSpoofingChange = useCallback(() => {
    setIsLoginSpoofingEnabled(!isLoginSpoofingEnabled);
  }, [isLoginSpoofingEnabled]);

  useMount(() => {
    if (!isAuthenticated) {
      if (
        window.location.pathname !== RoutePaths.INTERNAL_GRANULAR_ACCESS &&
        window.location.pathname !== RoutePaths.LOGIN &&
        window.location.pathname !== RoutePaths.HOME &&
        window.location.pathname !== RoutePaths.PASSWORD_RESET
      ) {
        sessionStorageSetTyped(RegrelloSessionStorageKey.REDIRECT_URL_AFTER_LOGIN, window.location.href);
      }
    }
  });

  useEffect(() => {
    if (isAuthenticated && isWaitingForAuth0Authentication) {
      setIsWaitingForAuth0Authentication(false);
    }
  }, [isAuthenticated, isWaitingForAuth0Authentication, setIsWaitingForAuth0Authentication]);

  const envState = useAsync(async () => {
    // TODO (clewis): This initializes feature flags for unauthenticated pages like Sign In, Sign
    // up, etc. This can lead to duplicate requests to /feature-flags when switching workspaces,
    // which is non-ideal, but this is how it already worked before I refactored feature flags to be
    // served by the backend, so I'm leaving this as is for now. Would be better to ensure that only
    // one request is made for feature flags, and it always successfully includes the workspace UUID
    // (not a guarantee at this spot in code right now when switching workspaces).
    try {
      await FeatureFlagService.init(null);
      setHasFeatureFlagError(false);
    } catch (_error) {
      // eslint-disable-next-line lingui/no-unlocalized-strings
      logErrorMessage("[Unexpected error] FeatureFlagsService did not initialize successfully.");
      setHasFeatureFlagError(true);
    }
    await initTranslation();

    // Initialize plugin registrars after core env setup and i18n are ready, but before rendering
    // app content that relies on field plugins/decorators.
    await initCustomFieldPluginRegistrar();
    await initSpectrumFieldPluginRegistrar();
  }, []);

  if (envState.loading) {
    return null;
  }

  if (hasFeatureFlagError) {
    return (
      <RegrelloNonIdealState
        actionProps={{
          text: t`Refresh`,
          onClick: () => {
            window.location.reload();
          },
        }}
        description={t`There was an error loading the Regrello platform. Please contact us if you continue to have trouble.`}
        size="large"
        title={t`Something went wrong`}
        visual={<RegrelloIcon iconName="alert" size="x-large" />}
      />
    );
  }

  if (!FeatureFlagService.isInitialized(null)) {
    return null;
  }

  return (
    <I18nProvider i18n={i18n}>
      <RegrelloAuthenticationProvider
        auth0Audience={config.Auth0Audience}
        auth0ClientID={config.Auth0ClientId}
        auth0Domain={config.Auth0Domain}
        isLoginSpoofingEnabled={isLoginSpoofingEnabled}
      >
        <MonitorAndReportFpsPerformance />
        {isAuthenticated ? (
          <Suspense fallback={null}>
            <LazyAppWithAuthProvider enableSentryPiiCollection={config.SentryEnablePII} />
          </Suspense>
        ) : isWaitingForAuth0Authentication ? null : (
          <BrowserRouter>
            <NuqsAdapter>
              <Routes>
                <Route
                  element={
                    <UnauthenticatedSignInPage
                      isLoginSpoofingEnabled={isLoginSpoofingEnabled}
                      onLoginSpoofingChange={handleLoginSpoofingChange}
                    />
                  }
                  path={RoutePaths.LOGIN}
                />
                <Route element={<UnauthenticatedCredentialsErrorPage />} path={RoutePaths.CREDENTIALS_ERROR} />
                <Route element={<UnauthenticatedSignInSsoPage />} path={RoutePaths.LOGIN_SSO} />
                <Route element={<UnauthenticatedSignUpPage />} path={RoutePaths.SIGN_UP} />
                <Route
                  element={<UnauthenticatedRequestForEmailPage mode="lostInvite" />}
                  path={getWorkspaceRouteTo(RoutePaths.INVITE_REQUEST)}
                />
                <Route element={<UnauthenticatedEmailSentPage mode="lostInvite" />} path={RoutePaths.INVITE_SENT} />
                <Route
                  element={<UnauthenticatedEmailSentPage mode="verificationSent" />}
                  path={RoutePaths.VERIFICATION_SENT}
                />
                <Route element={<UnauthenticatedPasswordReset />} path={RoutePaths.PASSWORD_RESET} />
                <Route
                  element={<UnauthenticatedRequestForEmailPage mode="passwordReset" />}
                  path={RoutePaths.PASSWORD_RESET_REQUEST}
                />
                <Route
                  element={<UnauthenticatedEmailSentPage mode="passwordReset" />}
                  path={RoutePaths.PASSWORD_RESET_SENT}
                />
                <Route
                  element={<UnauthenticatedRequestForEmailPage mode="mfaReset" />}
                  path={RoutePaths.MFA_RESET_REQUEST}
                />
                <Route element={<UnauthenticatedEmailSentPage mode="mfaReset" />} path={RoutePaths.MFA_RESET_SENT} />
                <Route element={<UnauthenticatedMFAResetPage />} path={RoutePaths.MFA_RESET} />
                <Route
                  element={
                    <Suspense fallback={null}>
                      <LazyWebformPage enableSentryPiiCollection={config.SentryEnablePII} />
                    </Suspense>
                  }
                  path={RoutePaths.SUBMISSION}
                />
                <Route element={<VerificationPage />} path={RoutePaths.REQUEST_VERIFICATION} />
                <Route element={<ConfirmVerification />} path={RoutePaths.CONFIRM_VERIFICATION} />
                <Route element={<UnsubscribePage />} path={RoutePaths.UNSUBSCRIBE} />

                <Route
                  element={
                    <Navigate
                      replace={true}
                      to={{
                        pathname: RoutePaths.LOGIN,
                        search: window.location.search,
                      }}
                    />
                  }
                  path="*"
                />
              </Routes>
            </NuqsAdapter>
          </BrowserRouter>
        )}
      </RegrelloAuthenticationProvider>
    </I18nProvider>
  );
});
